import React, {useState} from 'react';

import {EventCodeGenerationView} from 'src/modules/ai/event-code-generation/event-code-generation.view';
import {changeRequestValidation} from 'src/modules/ai/event-code-generation/validations/change-request-validation';
import {createTypingEffect} from 'src/modules/ai/utils/createTypingEffect';
import {
    serializeAttributeTypeFields,
} from 'src/modules/ai/utils/serializeAttributeTypeFields';
import {generateEventCode} from 'src/openapi/apis/EventDataGeneratorControllerApi';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppDispatch} from 'src/store';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

type Props = {
    eventChangeRequest: EventChangeRequest | undefined,
    onEventCodeGenerate?: (eventCode: string) => void,
};

const GENERATE_EVENT_CODE_ERROR_MESSAGE = 'Failed to generate event code. Please try again.';
const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const EventCodeGenerationController = (props: Props) => {
    const {
        eventChangeRequest,
        onEventCodeGenerate,
    } = props;

    const dispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();
    const [onGenerating, setOnGenerating] = useState(false);
    const [eventCode, setEventCode] = useState('');

    const onGenerateClick = async () => {
        if (eventChangeRequest) {
            if (changeRequestValidation(eventChangeRequest).length) {
                changeRequestValidation(eventChangeRequest).forEach(error => {
                    onCreateNote({
                        id: error,
                        status: Status.ERROR,
                        message: error,
                        buttonProps: {iconProps: {type: Icons.CLOSE}},
                        autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
                    });
                });
                return;
            }

            setOnGenerating(true);

            const request = {
                generateEventCodeRequest: {
                    ...serializeAttributeTypeFields(eventChangeRequest),
                    description: eventChangeRequest.description,
                    trigger: eventChangeRequest.trigger,
                    openApiDiscriminator: 'GenerateEventCodeRequest',
                },
            };

            const response = await dispatch(generateEventCode(request));

            if (response.payload.success) {
                createTypingEffect(response.payload.data, setEventCode);
            }

            if (response.payload.error) {
                onCreateNote({
                    id: 'generateEventCode',
                    status: Status.ERROR,
                    message: GENERATE_EVENT_CODE_ERROR_MESSAGE,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: 10000,
                });
            }

            setOnGenerating(false);
        }
    };

    return (
        <EventCodeGenerationView
            eventCode={eventCode}
            setEventCode={setEventCode}
            onGenerateClick={onGenerateClick}
            onGenerating={onGenerating}
            onEventCodeGenerate={onEventCodeGenerate}
        />
    );
};
